import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Search, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const LIMIT = 30;

// Admin → every listing on the platform, with PERMANENT delete (confirmation required).
export default function AdminListingsPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);   // single-row delete
  const [selected, setSelected] = useState([]);  // bulk selection (ids)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = (query = q, s = skip) => {
    setBusy(true);
    api.get("/admin/properties", { params: { q: query || undefined, limit: LIMIT, skip: s } })
      .then(({ data }) => setData(data)).catch(() => setData({ items: [], total: 0, counts: {} })).finally(() => setBusy(false));
  };
  useEffect(() => {
    const t = setTimeout(() => { setSkip(0); load(q, 0); }, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const confirmDelete = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      const { data: res } = await api.delete(`/admin/properties/${target.id}`);
      toast.success(`Deleted “${res.title}” — removed from listings, search, map and ${res.favorites_removed} favourite${res.favorites_removed === 1 ? "" : "s"}.`);
      setTarget(null);
      load(q, skip);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setDeleting(false); }
  };

  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      const { data: res } = await api.post("/admin/properties/bulk-delete", { ids: selected });
      toast.success(`Deleted ${res.deleted_count} listing${res.deleted_count === 1 ? "" : "s"} — removed from listings, search, map and ${res.favorites_removed} favourite${res.favorites_removed === 1 ? "" : "s"}.`);
      setSelected([]); setBulkOpen(false);
      load(q, skip);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setDeleting(false); }
  };

  const page = (s) => { setSkip(s); load(q, s); };
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const pageIds = (data?.items || []).map((p) => p.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected((prev) => (allOnPage ? prev.filter((id) => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]));
  const selectedItems = (data?.items || []).filter((p) => selected.includes(p.id));

  return (
    <section className="mt-12" data-testid="admin-listings">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Platform listings</div>
          <h2 className="font-display text-2xl mt-1 flex items-center gap-2"><Building2 size={20} /> All listings</h2>
          {data && <div className="text-xs text-[#5C6B7A] mt-1" data-testid="admin-listings-summary">{data.total} shown · {data.counts?.active ?? 0} active on site · {data.counts?.inactive ?? 0} hidden by owners</div>}
        </div>
        <div className="flex items-center gap-2 rounded-md border border-[#1A1F2C]/15 bg-white px-2">
          <Search size={14} className="text-[#5C6B7A]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, locality, city, owner…" className="border-0 shadow-none focus-visible:ring-0 px-0 w-64" data-testid="admin-listings-search" />
        </div>
      </div>

      {data && data.items.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#1A1F2C]/10 bg-white px-3 py-2" data-testid="admin-bulk-bar">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={allOnPage} onCheckedChange={toggleAll} data-testid="admin-select-all" /> Select all on this page
          </label>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#5C6B7A]" data-testid="admin-selected-count">{selected.length} selected</span>
            {selected.length > 0 && <Button variant="ghost" size="sm" onClick={() => setSelected([])} className="text-xs h-7" data-testid="admin-clear-selection">Clear</Button>}
            <Button size="sm" disabled={selected.length === 0} onClick={() => setBulkOpen(true)} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="admin-bulk-delete-btn">
              <Trash2 size={14} className="mr-1" /> Delete selected{selected.length ? ` (${selected.length})` : ""}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {!data ? <div className="p-6 text-sm text-[#5C6B7A]">Loading…</div> : data.items.length === 0 ? (
          <div className="p-6 text-sm text-[#5C6B7A] bg-[#F0EBE1] rounded-lg" data-testid="admin-listings-empty">No listings match.</div>
        ) : data.items.map((p) => (
          <div key={p.id} className={`flex items-center gap-4 rounded-lg p-3 border ${selected.includes(p.id) ? "bg-red-50 border-red-200" : "bg-[#F0EBE1] border-[#1A1F2C]/10"}`} data-testid={`admin-listing-${p.id}`}>
            <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggle(p.id)} aria-label={`Select ${p.title}`} data-testid={`admin-select-${p.id}`} />
            <div className="w-16 h-12 rounded overflow-hidden bg-[#1A1F2C]/10 shrink-0">
              {p.photos?.[0] && <img src={p.photos[0]} alt="" className="w-full h-full object-cover" loading="lazy" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/property/${p.id}`} className="font-medium text-sm hover:text-[#C85A32] truncate" data-testid={`admin-listing-title-${p.id}`}>{p.title}</Link>
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white text-[#5C6B7A]">{p.category}</span>
                {p.is_active === false && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#C85A32]/15 text-[#C85A32]">hidden</span>}
              </div>
              <div className="text-xs text-[#5C6B7A] mt-0.5 truncate">
                {p.locality}, {p.city} · {p.rent ? `${inr(p.rent)}/mo` : "—"} · Owner: {p.owner_name}{p.owner_email ? ` (${p.owner_email})` : ""} · {p.favorites_count} fav · {p.bookings_count} bookings
              </div>
            </div>
            <Button asChild variant="ghost" size="sm" className="rounded-md hidden sm:inline-flex"><Link to={`/property/${p.id}`} target="_blank" rel="noreferrer"><ExternalLink size={14} /></Link></Button>
            <Button variant="outline" size="sm" onClick={() => setTarget(p)} className="rounded-md text-red-600 border-red-200 hover:bg-red-50" data-testid={`admin-delete-listing-${p.id}`}>
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </div>
        ))}
      </div>

      {data && data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-between gap-4">
          <Button variant="outline" size="sm" disabled={skip === 0 || busy} onClick={() => page(Math.max(0, skip - LIMIT))} data-testid="admin-listings-prev">Previous</Button>
          <span className="text-xs text-[#5C6B7A]">{skip + 1}–{Math.min(skip + LIMIT, data.total)} of {data.total}</span>
          <Button variant="outline" size="sm" disabled={skip + LIMIT >= data.total || busy} onClick={() => page(skip + LIMIT)} data-testid="admin-listings-next">Next</Button>
        </div>
      )}

      <AlertDialog open={bulkOpen} onOpenChange={(v) => { if (!v && !deleting) setBulkOpen(false); }}>
        <AlertDialogContent data-testid="admin-bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.length} listing{selected.length === 1 ? "" : "s"} permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              These listings will be removed from the homepage, search, listings, map and every tenant&rsquo;s favourites. <strong>This cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-48 overflow-y-auto text-sm space-y-1 rounded-md bg-[#F0EBE1] p-3" data-testid="admin-bulk-delete-list">
            {selectedItems.map((p) => <li key={p.id} className="truncate">• {p.title} <span className="text-[#5C6B7A]">— {p.locality}, {p.city} · {p.owner_name}</span></li>)}
            {selected.length > selectedItems.length && <li className="text-[#5C6B7A]">…and {selected.length - selectedItems.length} more from other pages</li>}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmBulkDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-bulk-delete-confirm-btn">
              {deleting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Yes, delete {selected.length} listing{selected.length === 1 ? "" : "s"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!target} onOpenChange={(v) => { if (!v && !deleting) setTarget(null); }}>
        <AlertDialogContent data-testid="admin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this listing permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-[#1A1F2C]">{target?.title}</strong> ({target?.locality}, {target?.city}) by {target?.owner_name} will be removed from the homepage, search, listings, map and every tenant&rsquo;s favourites{target?.favorites_count ? ` (${target.favorites_count})` : ""}. <strong>This cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-delete-confirm-btn">
              {deleting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />} Yes, delete listing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
